import { Injectable, Injector } from '@angular/core';

import { LoggingService } from '../../logging/logic/logging.service';
import { ActionHandler, ActionHandlerType } from '../action-handler';
import { SyncService } from '../../sync/sync.service';
import { ActivityConfirmationsService } from '../../activity-confirmations/logic/activity-confirmations.service';
import { DynFormsService } from '../../dyn-forms/logic/dyn-forms.service';
import { DraftSyncEventsService } from '../../dyn-forms/draft-sync-events.service';
import { DraftPushNotifier, DraftPushParams } from './draft-push-notifier.service';

const I18N_PREFIX = 'SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.draft_finalized';

/**
 * APP4-948: the document was submitted for good on another device of this user, so the
 * draft lifecycle of that chain has ended. Distinct from draft_revoked, where the
 * document stays an editable draft and merely moves to another device.
 *
 * The cleanup deliberately does NOT wait for the sync. Submitting clears the draft
 * ownership, so the AC drops out of the draft branch of syncactivityconfirmations - and
 * for a form with draftextfiles=false or an ExtFileType with synctype `never` it does
 * not come back through the ExtFile branch either. This push is the only reliable
 * trigger, which is also why it is sent without the event-queue delay.
 *
 * Locally edited (dirty) drafts are deliberately KEPT. Their queued upload carries
 * checkacversionconflict with an acid that is no longer the chain head, which the
 * backend turns into a conflict version - that is what stops offline work from being
 * thrown away, and the user resolves it in the conflict dialog on the next claim.
 */
@Injectable({
  providedIn: 'root',
})
export class DraftFinalizedActionHandler implements ActionHandler {
  public readonly name: ActionHandlerType = 'draft_finalized';

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private syncService: SyncService,
    private draftPushNotifier: DraftPushNotifier,
    private draftSyncEvents: DraftSyncEventsService,
    private injector: Injector,
  ) {}

  // ActionHandler
  public async executeAction(params?: DraftPushParams): Promise<any> {
    const acukey = this.draftPushNotifier.getAcukey(params);
    const acId = this.draftPushNotifier.getAcId(params);
    const chainRootAcId = this.draftPushNotifier.getChainRootAcId(params);
    this.logger.log(
      this.constructor.name,
      'Draft finalized on another device - chainRootAcId',
      chainRootAcId,
      'acukey',
      acukey,
      'acId',
      acId,
      'params',
      params,
    );

    // First the cleanup, from the push alone: it must not depend on a sync that may
    // fail or may no longer deliver this AC at all.
    await this.discardLocalDraft(chainRootAcId, acukey, acId);

    // Tell an open form before the sync, so it stops offering the draft right away
    // instead of after the round trip.
    this.draftSyncEvents.draftFinalized$.next({ acId, acukey });

    let synced = true;
    try {
      await this.syncService.syncActivityConfirmations(true);
    } catch (error) {
      synced = false;
      this.logger.error(
        this.constructor.name,
        'Sync of activity confirmations failed, notifying with push params only',
        error?.message,
      );
    }

    // The finished AC is usually NOT in the result anymore (see class comment), so the
    // notification normally falls back to naming the form and the job from the push
    // params. When it is there, it adds who submitted it, when and on which device.
    const activityConfirmation = synced
      ? this.injector
          .get(ActivityConfirmationsService)
          .getActivityConfirmationById(acId)
      : undefined;

    await this.draftPushNotifier.notify(I18N_PREFIX, params, activityConfirmation);
  }

  // Private Functions
  private async discardLocalDraft(
    chainRootAcId: number | undefined,
    acukey: string | undefined,
    acId: number | undefined,
  ): Promise<void> {
    if (chainRootAcId == undefined && acukey == undefined) {
      // A document without a number range key, pushed by a backend from before KRK-45.
      // Matching on the acId instead would be wrong, not just incomplete: it belongs to
      // the submitted version, which this device never had.
      this.logger.warn(
        this.constructor.name,
        'draft_finalized without chainrootacid and without acukey (acId',
        acId,
        ') - cannot identify the local draft, skipping cleanup',
      );
      return;
    }

    try {
      await this.injector
        .get(DynFormsService)
        .deleteNotDirtyDraftsForChain(chainRootAcId, acukey);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Failed to discard local drafts of chain',
        chainRootAcId,
        acukey,
        error?.message,
      );
    }
  }
}
